import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const OFFSET_X = 12;
const OFFSET_Y = 14;

/**
 * App-safe tooltip: portals to document.body and follows the cursor with
 * position:fixed. Do NOT use MUI Tooltip for hover help — Popper placement
 * is broken by global CSS/theme in this app (tips land at top-left).
 *
 * Critical layout styles are inlined so tips still work if CSS is misplaced
 * (e.g. accidentally nested inside a mobile @media block).
 *
 * See docs/TOOLTIP_GUIDE.md.
 *
 * @param {object} props
 * @param {React.ReactNode} props.title - Tip content (string or node)
 * @param {React.ReactNode} props.children
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.fullWidth] - Stretch anchor to 100% (grid cells)
 * @param {string} [props.className] - Extra class on the tip bubble
 */
const pointerIsOver = (event, el) => {
  if (!el || !el.isConnected) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  return (
    event.clientX >= rect.left
    && event.clientX <= rect.right
    && event.clientY >= rect.top
    && event.clientY <= rect.bottom
  );
};

const CursorTooltip = ({ title, children, disabled = false, fullWidth = false, className = '' }) => {
  const [tip, setTip] = useState(null);
  const anchorRef = useRef(null);
  const active = !disabled && title != null && title !== '';

  const show = useCallback(
    (event) => {
      if (!active) return;
      setTip({
        x: event.clientX + OFFSET_X,
        y: event.clientY + OFFSET_Y,
      });
    },
    [active]
  );

  const move = useCallback((event) => {
    setTip((prev) =>
      prev
        ? {
            ...prev,
            x: event.clientX + OFFSET_X,
            y: event.clientY + OFFSET_Y,
          }
        : prev
    );
  }, []);

  const hide = useCallback(() => setTip(null), []);

  useEffect(() => {
    if (!active) setTip(null);
  }, [active]);

  useEffect(() => {
    if (!tip) return undefined;

    const hideUnlessOverAnchor = (event) => {
      if (!pointerIsOver(event, anchorRef.current)) setTip(null);
    };
    const hideOnWindowLeave = () => setTip(null);

    document.addEventListener('pointermove', hideUnlessOverAnchor, true);
    document.addEventListener('pointerdown', hideUnlessOverAnchor, true);
    document.documentElement.addEventListener('mouseleave', hideOnWindowLeave);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      document.removeEventListener('pointermove', hideUnlessOverAnchor, true);
      document.removeEventListener('pointerdown', hideUnlessOverAnchor, true);
      document.documentElement.removeEventListener('mouseleave', hideOnWindowLeave);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, [tip, hide]);

  if (!active) {
    return children;
  }

  return (
    <>
      <span
        ref={anchorRef}
        className={`app-cursor-tooltip-anchor${fullWidth ? ' app-cursor-tooltip-anchor--block' : ''}`}
        onMouseEnter={show}
        onMouseMove={move}
        onMouseLeave={hide}
      >
        {children}
      </span>
      {tip &&
        createPortal(
          <div
            className={`app-cursor-tooltip${className ? ` ${className}` : ''}`}
            style={{
              position: 'fixed',
              left: tip.x,
              top: tip.y,
              zIndex: 10000,
              pointerEvents: 'none',
              maxWidth: 280,
              padding: '6px 8px',
              borderRadius: 6,
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg-alt)',
              color: 'var(--color-text)',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.3,
              boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              whiteSpace: className.includes('project-overview-raci-cursor-tip') ? 'nowrap' : 'normal',
            }}
            role="tooltip"
          >
            {title}
          </div>,
          document.body
        )}
    </>
  );
};

export default CursorTooltip;
